import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import BenefitsSection from './components/BenefitsSection';
import ProcessSection from './components/ProcessSection';
import { useAuth } from '../../hooks/useAuth';
import { useSEO } from '../../utils/seo';
import { supabase } from '../../lib/supabase';

export default function BecomeCoach() {
  useSEO({
    title: 'Devenez Coach Sportif sur CoachFit - Recevez 80% de vos Réservations',
    description: 'Rejoignez notre équipe de coachs professionnels et développez votre activité. Commission de seulement 20%, paiements sécurisés via Stripe Connect, gestion simplifiée de vos clients. Inscrivez-vous gratuitement.',
    keywords: 'devenir coach sportif, recrutement coach, plateforme coaching, revenus coaching, coach indépendant, Stripe Connect',
    canonical: '/devenir-coach',
    ogImage: 'https://public.readdy.ai/ai/img_res/da0b8fa63c40e552541c080ae7451b0d.jpg',
    schema: {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "Coach Sportif Indépendant",
      "description": "Rejoignez CoachFit en tant que coach sportif indépendant et recevez 80% de vos réservations",
      "datePosted": "2024-01-01",
      "employmentType": "CONTRACTOR",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "CoachFit",
        "sameAs": `${import.meta.env.VITE_SITE_URL || ''}`
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "FR"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "EUR",
        "value": {
          "@type": "QuantitativeValue",
          "value": 80,
          "unitText": "PERCENT"
        }
      }
    }
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialty: '',
    experience: '',
    location: '',
    hourlyRate: '',
    description: '',
    languages: 'Français',
    certifications: ''
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  const { user } = useAuth();
  const navigate = useNavigate();

  // Pré-remplir les champs avec les données utilisateur
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        firstName: user.user_metadata?.first_name || '',
        lastName: user.user_metadata?.last_name || '',
        phone: user.user_metadata?.phone || ''
      }));
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setMessage('❌ Vous devez être connecté pour soumettre une candidature');
      return;
    }

    // Validation simple
    if (!formData.specialty || !formData.experience || !formData.location || !formData.hourlyRate) {
      setMessage('❌ Veuillez remplir tous les champs obligatoires (spécialité, expérience, zone, tarif)');
      return;
    }

    setLoading(true);
    setMessage('');
    setSuccess(false);

    try {
      console.log('🔐 User connecté:', user.email);

      // 1. Upload de l'avatar si présent
      let uploadedAvatarUrl = '';
      if (avatarFile) {
        try {
          const fileExt = avatarFile.name.split('.').pop();
          const fileName = `${user.id}-${Date.now()}.${fileExt}`;
          const filePath = `avatars/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, avatarFile, { upsert: true });

          if (!uploadError) {
            const { data } = supabase.storage
              .from('avatars')
              .getPublicUrl(filePath);
            uploadedAvatarUrl = data.publicUrl;
            console.log('✅ Avatar uploadé:', uploadedAvatarUrl);
          }
        } catch (err) {
          console.log('⚠️ Erreur upload avatar (non bloquant):', err);
        }
      }

      // 2. Préparer les données
      const coachData = {
        specialty: formData.specialty,
        experienceYears: parseInt(formData.experience) || 0,
        hourlyRate: parseFloat(formData.hourlyRate) || 0,
        location: formData.location,
        description: formData.description || `Coach ${formData.specialty} avec ${formData.experience} ans d'expérience`,
        languages: formData.languages.split(',').map(l => l.trim()).filter(l => l),
        certifications: formData.certifications.split(',').map(c => c.trim()).filter(c => c),
        avatarUrl: uploadedAvatarUrl || null,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      };

      console.log('📤 Envoi des données:', coachData);

      // 3. Récupérer le token d'authentification
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setMessage('❌ Session expirée. Veuillez vous reconnecter.');
        setTimeout(() => window.REACT_APP_NAVIGATE('/connexion'), 2000);
        setLoading(false);
        return;
      }

      // 4. Appel à l'edge function
      const response = await supabase.functions.invoke('coach-registration', {
        body: coachData,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      console.log('📥 Réponse complète:', response);

      // 5. Vérifier les erreurs
      if (response.error) {
        console.error('❌ Erreur:', response.error);
        
        // Erreur d'authentification
        if (response.error.message?.includes('JWT') || response.error.message?.includes('auth')) {
          setMessage('❌ Session expirée. Veuillez vous reconnecter.');
          setTimeout(() => window.REACT_APP_NAVIGATE('/connexion'), 2000);
          setLoading(false);
          return;
        }
        
        // Autre erreur
        setMessage(`❌ ${response.error.message || 'Une erreur est survenue'}`);
        setLoading(false);
        return;
      }

      // 6. Vérifier la réponse
      if (!response.data) {
        console.error('❌ Pas de données dans la réponse');
        setMessage('❌ Erreur serveur: aucune réponse reçue');
        setLoading(false);
        return;
      }

      // 7. Vérifier le succès
      if (response.data.success === false) {
        console.log('⚠️ Erreur métier:', response.data.error);
        setMessage(`⚠️ ${response.data.error || 'Une erreur est survenue'}`);
        setLoading(false);
        return;
      }

      // 8. SUCCÈS ! 
      console.log('🎉 Candidature envoyée avec succès !');

      // Soumettre au formulaire Readdy (en arrière-plan)
      try {
        const formDataToSubmit = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
          formDataToSubmit.append(key, String(value));
        });

        fetch('https://readdy.ai/api/form/d4pearfiu80n4l8qnp90', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formDataToSubmit.toString()
        }).catch(() => {});
      } catch (err) {
        console.log('⚠️ Erreur formulaire Readdy (non bloquant)');
      }

      // 9. Afficher le succès
      setSuccess(true);
      setShowConfetti(true);
      setMessage('');
      
      // Réinitialiser le formulaire
      setFormData({
        firstName: user.user_metadata?.first_name || '',
        lastName: user.user_metadata?.last_name || '',
        email: user.email || '',
        phone: user.user_metadata?.phone || '',
        specialty: '',
        experience: '',
        location: '',
        hourlyRate: '',
        description: '',
        languages: 'Français',
        certifications: ''
      });
      setAvatarPreview('');
      setAvatarFile(null);

      // Arrêter les confettis après 4 secondes
      setTimeout(() => setShowConfetti(false), 4000);

      // Redirection après 5 secondes
      setTimeout(() => {
        window.REACT_APP_NAVIGATE('/mes-reservations');
      }, 5000);

    } catch (err: any) {
      console.error('💥 Erreur catch:', err);
      console.error('Stack:', err.stack);
      setMessage(`❌ ${err.message || 'Une erreur inattendue est survenue. Veuillez réessayer.'}`);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-20 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.85), rgba(5, 150, 105, 0.85)), url('https://readdy.ai/api/search-image?query=Professional%20fitness%20coach%20training%20client%20in%20modern%20gym%2C%20motivational%20atmosphere%2C%20coaching%20session%2C%20personal%20trainer%20helping%20client%2C%20bright%20lighting%2C%20success%20and%20achievement&width=1200&height=600&seq=coach-hero&orientation=landscape')`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Devenez Coach sur CoachFit
            </h1>
            <p className="text-xl text-green-50 max-w-3xl mx-auto">
              Rejoignez notre équipe de coachs professionnels et recevez 80% de chaque réservation
            </p>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Formulaire de candidature
                </h2>
                <p className="text-lg text-gray-600">
                  Remplissez ce formulaire pour rejoindre notre équipe
                </p>
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800">
                    <i className="ri-money-euro-circle-line mr-2"></i>
                    <strong>Vous recevez 80%</strong> de chaque réservation
                  </p>
                </div>
              </div>

              {/* Message d'erreur */}
              {message && !success && (
                <div className="mb-6 p-4 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200 flex items-start">
                  <i className="ri-error-warning-line text-xl mr-3 flex-shrink-0"></i>
                  <span>{message}</span>
                </div>
              )}

              {/* Message de succès */}
              {success && (
                <div className="mb-6 p-6 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full mr-4 animate-bounce">
                      <i className="ri-checkbox-circle-fill text-white text-2xl"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-green-800 mb-2">
                        🎉 Candidature envoyée avec succès !
                      </h3>
                      <p className="text-sm text-green-700 mb-2">
                        Votre demande est en attente de validation par notre équipe.
                      </p>
                      <p className="text-xs text-green-600">
                        <i className="ri-time-line mr-1"></i>
                        Vous serez notifié par email dès validation.
                      </p>
                      <p className="text-xs text-green-600 mt-2">
                        <i className="ri-arrow-right-line mr-1"></i>
                        Redirection dans quelques secondes...
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 h-1 bg-green-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 animate-progress"></div>
                  </div>
                </div>
              )}

              {!user && (
                <div className="mb-6 p-4 rounded-lg bg-yellow-50 text-yellow-800 border border-yellow-200 flex items-start">
                  <i className="ri-information-line text-xl mr-3 flex-shrink-0"></i>
                  <div>
                    Vous devez être connecté pour postuler. 
                    <a href="/connexion" className="ml-1 font-medium hover:underline">
                      Se connecter
                    </a>
                  </div>
                </div>
              )}

              <form id="coach-application-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                {/* Informations personnelles */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="ri-user-line mr-2 text-green-600"></i>
                    Informations personnelles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        Prénom
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="Jean"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="Dupont"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                        readOnly
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations professionnelles */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="ri-briefcase-line mr-2 text-green-600"></i>
                    Informations professionnelles
                    <span className="ml-2 text-xs font-normal text-red-600">* Obligatoire</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-2">
                        Spécialité <span className="text-red-600">*</span>
                      </label>
                      <select
                        id="specialty"
                        name="specialty"
                        value={formData.specialty}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="Musculation">Musculation</option>
                        <option value="Yoga">Yoga & Pilates</option>
                        <option value="Nutrition">Nutrition</option>
                        <option value="Cardio">Cardio</option>
                        <option value="HIIT">HIIT</option>
                        <option value="Sport Prénatal">Sport Prénatal/Postnatal</option>
                        <option value="Coaching Senior">Coaching Senior</option>
                        <option value="Préparation Sportive">Préparation Sportive</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                        Années d'expérience <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="number"
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleInputChange}
                        required
                        min="0"
                        max="50"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="5"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                        Zone d'intervention <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="Paris, Lyon..."
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700 mb-2">
                        Tarif horaire (€) <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="number"
                        id="hourlyRate"
                        name="hourlyRate"
                        value={formData.hourlyRate}
                        onChange={handleInputChange}
                        required
                        min="30"
                        max="200"
                        step="5"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="80"
                      />
                      <p className="text-xs text-gray-500 mt-1">Vous recevrez 80%</p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                      Description <span className="text-gray-500 text-xs">(optionnel)</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Décrivez votre approche..."
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500</p>
                  </div>
                </div>

                {/* Informations complémentaires */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <i className="ri-file-list-line mr-2 text-green-600"></i>
                    Informations complémentaires
                    <span className="ml-2 text-xs font-normal text-gray-500">(optionnel)</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="languages" className="block text-sm font-medium text-gray-700 mb-2">
                        Langues parlées
                      </label>
                      <input
                        type="text"
                        id="languages"
                        name="languages"
                        value={formData.languages}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="Français, Anglais..."
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="certifications" className="block text-sm font-medium text-gray-700 mb-2">
                        Certifications
                      </label>
                      <input
                        type="text"
                        id="certifications"
                        name="certifications"
                        value={formData.certifications}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="BPJEPS, CQP..."
                      />
                    </div>
                  </div>
                </div>

                {/* Photo de profil */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photo de profil <span className="text-gray-500 text-xs">(optionnel)</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Aperçu" className="w-full h-full object-cover" />
                      ) : (
                        <i className="ri-user-line text-3xl text-gray-400"></i>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                      <label 
                        htmlFor="image-upload" 
                        className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-upload-2-line"></i>
                        {avatarPreview ? 'Modifier' : 'Ajouter une photo'}
                      </label>
                      <p className="mt-2 text-xs text-gray-500">
                        JPG, PNG ou GIF. Max 5 MB.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading || !user}
                    className="w-full bg-green-600 text-white py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i>
                        Envoyer ma candidature
                      </>
                    )}
                  </button>
                  <p className="text-xs text-center text-gray-500 mt-3">
                    En soumettant ce formulaire, vous acceptez nos conditions
                  </p>
                </div>
              </form>
            </div>
          </div>
        </section>

        <BenefitsSection />
        <ProcessSection />
      </main>
      <Footer />

      {/* Animation de confettis */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10%',
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: ['#10b981', '#059669', '#34d399', '#6ee7b7'][Math.floor(Math.random() * 4)],
                  transform: `rotate(${Math.random() * 360}deg)`
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
